import { useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import styled from 'styled-components'
import { ChevronLeft, Search } from 'lucide-react'
import { useQuery } from '@tanstack/react-query'
import { MobileColors } from 'styles/mobileTokens'
import { BackButton } from 'components/MobileSearch/MobileSearch.styles'
import useApiService from 'services/api'
import { Spinner } from 'react-bootstrap'
import { Menu } from 'models/menus'

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`

const HeaderSection = styled.div`
  padding: 16px;
  border-bottom: 1px solid ${MobileColors.borderDefault};
  background: #f9fafb;
`

const HeaderLabel = styled.div`
  font-size: 12px;
  font-weight: 400;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 4px;
`

const ChainName = styled.h1`
  font-size: 20px;
  font-weight: 600;
  margin: 0 0 4px 0;
  color: #191d23;
`

const ItemCount = styled.div`
  font-size: 12px;
  color: #6b7280;
`

const SearchSection = styled.div`
  padding: 12px 20px;
`

const SearchInputContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  padding: 8px 12px;

  &:focus-within {
    border-color: #2c6b4e;
    background: white;
  }
`

const SearchInput = styled.input`
  flex: 1;
  border: none;
  background: transparent;
  font-size: 14px;
  color: #374151;
  outline: none;

  &::placeholder {
    color: #9ca3af;
  }
`

const ListArea = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 0 20px;
`

const MenuItemCard = styled.div`
  padding: 12px 0;
  border-bottom: 1px solid ${MobileColors.borderDefault};
`

const MenuItemName = styled.div`
  font-size: 15px;
  font-weight: 600;
  color: #191d23;
  margin-bottom: 4px;
`

const MenuItemDescription = styled.div`
  font-size: 13px;
  color: #6b7280;
  line-height: 1.4;
  margin-bottom: 6px;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`

const MenuItemPrice = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #2c6b4e;
`

export function MobileMenuPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const api = useApiService()

  // Query params
  const chainId = searchParams.get('chain_id')
  const chainName = searchParams.get('chain_name') || 'Menu'

  // Search state
  const [searchQuery, setSearchQuery] = useState('')

  // Fetch menu items
  const {
    data: menuItems,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['mobileMenuPage', chainId],
    queryFn: () => api.getChainMenuList(Number(chainId)),
    enabled: !!chainId,
    staleTime: 5 * 60 * 1000,
  })

  // Filter menu items by search
  const filteredItems = useMemo(() => {
    if (!menuItems) return []
    if (!searchQuery.trim()) return menuItems

    const query = searchQuery.toLowerCase()
    return menuItems.filter(
      (item: Menu) =>
        item.name?.toLowerCase().includes(query) ||
        item.description?.toLowerCase().includes(query)
    )
  }, [menuItems, searchQuery])

  const handleBack = () => {
    navigate(-1)
  }

  const formatPrice = (price: number | string | null | undefined) => {
    if (price === null || price === undefined || price === '') return null
    const numPrice = typeof price === 'string' ? parseFloat(price) : price
    if (isNaN(numPrice)) return price
    return `$${numPrice.toFixed(2)}`
  }

  return (
    <PageContainer>
      <BackButton onClick={handleBack}>
        <ChevronLeft size={20} />
        Back
      </BackButton>

      <HeaderSection>
        <HeaderLabel>MENU</HeaderLabel>
        <ChainName>{chainName}</ChainName>
        <ItemCount>{menuItems?.length || 0} items</ItemCount>
      </HeaderSection>

      {/* Search Input */}
      <SearchSection>
        <SearchInputContainer>
          <Search size={18} color="#9ca3af" />
          <SearchInput
            type="text"
            placeholder="Search by keyword"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </SearchInputContainer>
      </SearchSection>

      <ListArea>
        {/* Loading state */}
        {isLoading && (
          <div className="flex items-center justify-center py-8">
            <Spinner animation="border" size="sm" />
          </div>
        )}

        {/* Error state */}
        {!!error && (
          <div className="py-8 text-center text-sm text-red-500">
            Failed to load menu
          </div>
        )}

        {/* Empty state */}
        {!error && !isLoading && filteredItems.length === 0 && (
          <div className="py-8 text-center text-sm text-gray-500">
            {searchQuery
              ? 'No items match your search'
              : 'No menu items available'}
          </div>
        )}

        {/* Menu Items */}
        {!error && filteredItems.length > 0 && (
          <div>
            {filteredItems.map((item: Menu) => (
              <MenuItemCard key={item.id}>
                <MenuItemName>{item.name}</MenuItemName>
                {item.description && (
                  <MenuItemDescription>{item.description}</MenuItemDescription>
                )}
                {formatPrice(item.price) && (
                  <MenuItemPrice>{formatPrice(item.price)}</MenuItemPrice>
                )}
              </MenuItemCard>
            ))}
          </div>
        )}
      </ListArea>
    </PageContainer>
  )
}
